import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Reusable calendar navigation bar.
 * Props:
 *  - cursor: Date (anchor for the current view)
 *  - onCursor(newDate)
 *  - view: "month" | "week" (optional)
 *  - onView(newView) — if omitted, view toggle is hidden
 *  - label: optional override for the current-period label
 *  - prefix: optional left-side React node (title etc.)
 *  - yearRange: number of years +/- current to include in the year picker (default 3)
 */
export default function CalendarNav({
  cursor,
  onCursor,
  view,
  onView,
  label,
  prefix,
  yearRange = 3,
  testIdPrefix = "cal",
}) {
  const today = new Date();
  const month = cursor.getMonth();
  const year = cursor.getFullYear();
  const displayLabel = label || cursor.toLocaleString(undefined, {
    month: view === "week" ? undefined : "long",
    year: "numeric",
    day: view === "week" ? "numeric" : undefined,
  });

  const step = (delta) => {
    if (view === "week") {
      const d = new Date(cursor);
      d.setDate(d.getDate() + delta * 7);
      onCursor(d);
    } else {
      onCursor(new Date(year, month + delta, 1));
    }
  };
  const goToday = () => {
    if (view === "week") {
      // snap to start of this week (Monday)
      const d = new Date(today);
      const dow = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - dow);
      onCursor(d);
    } else {
      onCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    }
  };

  const years = [];
  for (let y = today.getFullYear() - yearRange; y <= today.getFullYear() + yearRange; y++) years.push(y);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-nav`}>
      {prefix}
      <h3 className="font-heading text-xl font-semibold flex items-center gap-2" data-testid={`${testIdPrefix}-nav-label`}>
        <CalendarDays className="w-4 h-4 text-muted-foreground" />
        {displayLabel}
      </h3>
      <div className="flex items-center gap-1 ml-auto flex-wrap">
        {/* Jump to month */}
        <Select value={String(month)} onValueChange={(v) => onCursor(new Date(year, parseInt(v, 10), 1))}>
          <SelectTrigger data-testid={`${testIdPrefix}-nav-month`} className="h-9 w-[130px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            {MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Jump to year */}
        <Select value={String(year)} onValueChange={(v) => onCursor(new Date(parseInt(v, 10), month, 1))}>
          <SelectTrigger data-testid={`${testIdPrefix}-nav-year`} className="h-9 w-[100px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* View toggle */}
        {onView && (
          <div className="inline-flex border border-border rounded-md overflow-hidden ml-1">
            {["week", "month"].map((v) => (
              <button
                key={v}
                data-testid={`${testIdPrefix}-nav-view-${v}`}
                onClick={() => onView(v)}
                className={`text-xs px-2.5 py-1.5 transition-colors ${
                  view === v ? "bg-primary text-primary-foreground" : "bg-[#1E1E1E] hover:bg-[#141414]"
                }`}
              >{v[0].toUpperCase() + v.slice(1)}</button>
            ))}
          </div>
        )}
        <Button data-testid={`${testIdPrefix}-nav-prev`} size="icon" variant="outline" onClick={() => step(-1)}>
          <ChevronLeft className="w-4 h-4"/>
        </Button>
        <Button data-testid={`${testIdPrefix}-nav-today`} size="sm" variant="outline" onClick={goToday}>Today</Button>
        <Button data-testid={`${testIdPrefix}-nav-next`} size="icon" variant="outline" onClick={() => step(1)}>
          <ChevronRight className="w-4 h-4"/>
        </Button>
      </div>
    </div>
  );
}
